import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import "../KHPBTT/DashboarData.css";
import NavbarTrungThu from "../Layout/NavbarTrungThu";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const DashboardBkem = () => {
  const [selectedStartDate, setSelectedStartDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedEndDate, setSelectedEndDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedStores] = useState([77,58,65,60,25,80,79,73,55,37,46,16,76,62,42,83,15,47,39,51,69,67,68,75,66,90,74,78,41,59,105,108,112,116,117,118,119,120,122,128,123,124,127,129,131,132,133,136,137,138,139,140,141,142,144,145,147,148,149,150,156,157,158,160,162,1000,1001,164,22,63,24,56,43,53,111,48,17,84,36,27,57,50,70,64,71,61,31,72,32,54]); // Fixed stores for cake business
  
  const isFetchingRef = useRef(false);
  const [dashboardData, setDashboardData] = useState({
    retailData: null,
    wholesaleData: null,
    dailySalesData: null,
    loading: true,
    error: null,
  });

  const [loadingStates, setLoadingStates] = useState({
    retail: true,
    wholesale: true,
    dailySales: true,
    topStores: true,
    inventory: true, // Add inventory loading state
  });

  // Add state for top stores data
  const [topStoresData, setTopStoresData] = useState(null);

  // Add state for inventory data
  const [inventoryData, setInventoryData] = useState(null);

  // Fetch data from APIs
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (isFetchingRef.current) return;

      try {
        isFetchingRef.current = true;
        setDashboardData((prev) => ({ ...prev, loading: true, error: null }));
        setLoadingStates({
          retail: true,
          wholesale: true,
          dailySales: true,
          topStores: true,
          inventory: true, // Add to initial loading states
        });

        const requestBody = {
          MaCH: selectedStores,
          NgayBatDau: selectedStartDate,
          NgayKetThuc: selectedEndDate
        };

        // Calculate 7 days range for daily sales (always 7 days before end date)
        const endDate = new Date(selectedEndDate);
        const startDateFor7Days = new Date(endDate);
        startDateFor7Days.setDate(startDateFor7Days.getDate() - 6); // 7 days including end date
        
        const dailySalesBody = {
          MaCH: selectedStores,
          NgayBatDau: startDateFor7Days.toISOString().split("T")[0],
          NgayKetThuc: selectedEndDate
        };

        // Fetch retail data
        const retailResponse = await fetch("https://doticom.vn:3000/api/cake-retail-summary", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody)
        });
        const retailData = await retailResponse.json();
        setLoadingStates(prev => ({ ...prev, retail: false }));

        // Fetch wholesale data
        const wholesaleResponse = await fetch("https://doticom.vn:3000/api/cake-wholesale-summary", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody)
        });
        const wholesaleData = await wholesaleResponse.json();
        setLoadingStates(prev => ({ ...prev, wholesale: false }));

        // Fetch daily sales data for trends
        const dailySalesResponse = await fetch("https://doticom.vn:3000/api/cake-daily-sales", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(dailySalesBody)
        });
        const dailySalesData = await dailySalesResponse.json();
        setLoadingStates(prev => ({ ...prev, dailySales: false }));

        // Fetch top stores data
        const topStoresResponse = await fetch("https://doticom.vn:3000/api/cake-top-stores", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody)
        });
        const topStoresResult = await topStoresResponse.json();
        setTopStoresData(topStoresResult);
        setLoadingStates(prev => ({ ...prev, topStores: false }));

        // Fetch inventory data
        const inventoryResponse = await fetch("https://doticom.vn:3000/api/cake-inventory", {
          method: "GET",
          headers: { "Content-Type": "application/json" }
        });
        const inventoryResult = await inventoryResponse.json();
        setInventoryData(inventoryResult);
        setLoadingStates(prev => ({ ...prev, inventory: false }));

        setDashboardData({
          retailData,
          wholesaleData,
          dailySalesData,
          loading: false,
          error: null,
        });

      } catch (error) {
        console.error("Error fetching cake dashboard data:", error);
        setDashboardData((prev) => ({
          ...prev,
          loading: false,
          error: "Không thể tải dữ liệu bánh kem",
        }));
        setLoadingStates({
          retail: false,
          wholesale: false,
          dailySales: false,
          topStores: false,
          inventory: false, // Add to error state reset
        });
      } finally {
        isFetchingRef.current = false;
      }
    };

    fetchDashboardData();
  }, [selectedStartDate, selectedEndDate]);

  // Calculate dashboard metrics from API data
  const calculateDashboardMetrics = () => {
    const { retailData, wholesaleData, dailySalesData } = dashboardData;

    if (dashboardData.loading || dashboardData.error) {
      return {
        totalQuantity: 0,
        totalRevenue: 0,
        retailQuantity: 0,
        wholesaleQuantity: 0,
        smallCakes: 0,
        largeCakes: 0,
        salesTrend: [0, 0, 0, 0, 0, 0, 0],
        orderTrend: [0, 0, 0, 0, 0, 0, 0],
        cakeTrends: { small: [0, 0, 0, 0, 0, 0, 0], large: [0, 0, 0, 0, 0, 0, 0] },
        revenue: [0, 0, 0, 0, 0, 0, 0],
        last7DaysLabels: [],
      };
    }

    // Calculate current period metrics (sum of selected date range)
    const totalQuantity = (retailData?.SoLuong || 0) + (wholesaleData?.SoLuong || 0);
    const totalRevenue = (retailData?.TongTien || 0) + (wholesaleData?.TongTien || 0);
    const retailQuantity = retailData?.SoLuong || 0;
    const wholesaleQuantity = wholesaleData?.SoLuong || 0;
    const smallCakes = (retailData?.ChiTiet?.BanhKemNho || 0) + (wholesaleData?.ChiTiet?.BanhKemNho || 0);
    const largeCakes = (retailData?.ChiTiet?.BanhKemLon || 0) + (wholesaleData?.ChiTiet?.BanhKemLon || 0);

    // Always calculate trends for 7 days before end date
    const last7Days = [];
    const endDate = new Date(selectedEndDate);
    for (let i = 6; i >= 0; i--) {
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      last7Days.push(date.toISOString().split("T")[0]);
    }

    const salesTrend = [];
    const orderTrend = [];
    const smallCakeTrend = [];
    const largeCakeTrend = [];
    const revenueTrend = [];
    const last7DaysLabels = [];

    last7Days.forEach(dateStr => {
      const date = new Date(dateStr);
      last7DaysLabels.push(`${date.getMonth() + 1}/${date.getDate()}`);

      // Find data for this date, default to 0 if not found
      const retailForDay = dailySalesData?.BanLe?.find(item => item.Ngay === dateStr) || {
        SoLuong: 0,
        TongTien: 0,
        ChiTiet: { BanhKemNho: 0, BanhKemLon: 0 }
      };
      const wholesaleForDay = dailySalesData?.BanSi?.find(item => item.Ngay === dateStr) || {
        SoLuong: 0,
        TongTien: 0,
        ChiTiet: { BanhKemNho: 0, BanhKemLon: 0 }
      };

      const dailyRetailQty = retailForDay.SoLuong || 0;
      const dailyWholesaleQty = wholesaleForDay.SoLuong || 0;
      const dailyRetailRevenue = retailForDay.TongTien || 0;
      const dailyWholesaleRevenue = wholesaleForDay.TongTien || 0;

      salesTrend.push(dailyRetailQty);
      orderTrend.push(dailyWholesaleQty);
      
      const dailySmallCakes = (retailForDay.ChiTiet?.BanhKemNho || 0) + (wholesaleForDay.ChiTiet?.BanhKemNho || 0);
      const dailyLargeCakes = (retailForDay.ChiTiet?.BanhKemLon || 0) + (wholesaleForDay.ChiTiet?.BanhKemLon || 0);
      
      smallCakeTrend.push(dailySmallCakes);
      largeCakeTrend.push(dailyLargeCakes);
      
      revenueTrend.push((dailyRetailRevenue + dailyWholesaleRevenue) / 1000000); // Convert to millions
    });

    return {
      totalQuantity,
      totalRevenue,
      retailQuantity,
      wholesaleQuantity,
      smallCakes,
      largeCakes,
      salesTrend,
      orderTrend,
      cakeTrends: { small: smallCakeTrend, large: largeCakeTrend },
      revenue: revenueTrend,
      last7DaysLabels,
    };
  };

  const currentData = calculateDashboardMetrics();

  // Chart data configurations
  const salesTrendData = {
    labels: currentData.last7DaysLabels,
    datasets: [
      {
        label: "Bánh bán",
        data: currentData.salesTrend,
        backgroundColor: "#1e3a8a",
      },
      {
        label: "Bánh Đặt",
        data: currentData.orderTrend,
        backgroundColor: "#fbbf24",
      },
    ],
  };

  const salesTrendByTypeData = {
    labels: currentData.last7DaysLabels,
    datasets: [
      {
        label: "Bánh kem nhỏ",
        data: currentData.cakeTrends.small,
        backgroundColor: "#60a5fa",
      },
      {
        label: "Bánh kem lớn",
        data: currentData.cakeTrends.large,
        backgroundColor: "#ef4444",
      },
    ],
  };

  const revenueData = {
    labels: currentData.last7DaysLabels,
    datasets: [
      {
        label: "Doanh số (triệu đồng)",
        data: currentData.revenue,
        borderColor: "#3b82f6",
        backgroundColor: "#3b82f6",
        tension: 0.4,
      },
    ],
  };

  const cakeTypeDistribution = {
    labels: ["Bánh kem nhỏ", "Bánh kem lớn"],
    datasets: [
      {
        data: inventoryData ? [
          inventoryData.Tong?.ChiTiet?.BanhKemNho || 0,
          inventoryData.Tong?.ChiTiet?.BanhKemLon || 0
        ] : [0, 0],
        backgroundColor: ["#60a5fa", "#ef4444"],
      },
    ],
  };

  // Add top stores chart data configuration
  const topStoresChartData = {
    labels: topStoresData ? topStoresData.slice(0, 20).map(store => store.MaBravo || `CH${store.MaCH}`) : [],
    datasets: [
      {
        label: "Số lượng bán",
        data: topStoresData ? topStoresData.slice(0, 20).map(store => store.SoLuong) : [],
        backgroundColor: "#3b82f6",
        borderColor: "#1d4ed8",
        borderWidth: 1,
      },
    ],
  };

    // Add top inventory stores chart data configuration
  const topInventoryStoresChartData = {
    labels: inventoryData ? inventoryData.NhomBravo
      .flatMap(group => group.CuaHang)
      .sort((a, b) => b.SoLuong - a.SoLuong)
      .slice(0, 20)
      .map(store => store.MaBravo || `CH${store.MaCH}`) : [],
    datasets: [
      {
        label: "Số lượng tồn",
        data: inventoryData ? inventoryData.NhomBravo
          .flatMap(group => group.CuaHang)
          .sort((a, b) => b.SoLuong - a.SoLuong)
          .slice(0, 20)
          .map(store => store.SoLuong) : [],
        backgroundColor: "#10b981",
        borderColor: "#059669",
        borderWidth: 1,
      },
    ],
  };

  const topStoresOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const store = topStoresData[context.dataIndex];
            return [
              `Số lượng: ${store.SoLuong} cái`,
              `Doanh thu: ${store.TongTien.toLocaleString('vi-VN')} đ`,
              `Bánh nhỏ: ${store.ChiTiet.BanhKemNho} cái`,
              `Bánh lớn: ${store.ChiTiet.BanhKemLon} cái`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        grid: {
          display: true,
        },
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return value + ' cái';
          }
        }
      },
    },
  };

  const topInventoryStoresOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const allStores = inventoryData.NhomBravo
              .flatMap(group => group.CuaHang)
              .sort((a, b) => b.SoLuong - a.SoLuong);
            const store = allStores[context.dataIndex];
            return [
              `Số lượng tồn: ${store.SoLuong} cái`,
              `Giá trị tồn: ${store.TongTien.toLocaleString('vi-VN')} đ`,
              `Bánh nhỏ: ${store.ChiTiet.BanhKemNho} cái`,
              `Bánh lớn: ${store.ChiTiet.BanhKemLon} cái`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        grid: {
          display: true,
        },
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return value + ' cái';
          }
        }
      },
    },
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        grid: {
          display: true,
        },
        beginAtZero: true,
      },
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return value + "M";
          },
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
  };

  return (
    <>
      <NavbarTrungThu />
      <div className="dashboard">
        <div className="dashboard-header">
          <div className="time-filter">
            <label style={{ marginRight: '10px', fontSize: '14px', fontWeight: '500' }}>
              Từ ngày:
            </label>
            <input
              type="date"
              value={selectedStartDate}
              onChange={(e) => setSelectedStartDate(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                backgroundColor: "white",
                fontSize: "14px",
                marginRight: "15px",
              }}
            />
            <label style={{ marginRight: '10px', fontSize: '14px', fontWeight: '500' }}>
              Đến ngày:
            </label>
            <input
              type="date"
              value={selectedEndDate}
              onChange={(e) => setSelectedEndDate(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                backgroundColor: "white",
                fontSize: "14px",
              }}
            />
          </div>
        </div>

        {/* KPI Cards */}
        <div className="kpi-section">
          <div className="kpi-card">
            <h3>Tổng số lượng bánh kem</h3>
            <div className="kpi-circle" style={{"--progress-angle": "360deg"}}>
              <span className="kpi-number">{currentData.totalQuantity}</span>
            </div>
            <div className="kpi-details">
            </div>
          </div>

          <div className="kpi-card">
            <h3>Doanh thu bánh kem</h3>
            <div className="kpi-circle" style={{"--progress-angle": "360deg"}}>
              <span className="kpi-number">{(currentData.totalRevenue / 1000000).toFixed(1)}M</span>
            </div>
            <div className="kpi-details">
           
            </div>
          </div>

          <div className="kpi-card">
            <h3>Phân bổ theo hình thức bán</h3>
            <div className="kpi-bars">
              <div className="kpi-bar">
                <span>Bánh Bán</span>
                <div className="bar-container">
                  <div
                    className="bar"
                    style={{
                      width: currentData.totalQuantity > 0 
                        ? `${(currentData.retailQuantity / currentData.totalQuantity) * 100}%`
                        : "0%",
                      backgroundColor: "#1e3a8a",
                    }}
                  >
                    {currentData.retailQuantity}
                  </div>
                </div>
                <span>{currentData.totalQuantity > 0 
                  ? Math.round((currentData.retailQuantity / currentData.totalQuantity) * 100)
                  : 0}%</span>
              </div>
              <div className="kpi-bar">
                <span>Đặt</span>
                <div className="bar-container">
                  <div
                    className="bar"
                    style={{
                      width: currentData.totalQuantity > 0 
                        ? `${(currentData.wholesaleQuantity / currentData.totalQuantity) * 100}%`
                        : "0%",
                      backgroundColor: "#fbbf24",
                    }}
                  >
                    {currentData.wholesaleQuantity}
                  </div>
                </div>
                <span>{currentData.totalQuantity > 0 
                  ? Math.round((currentData.wholesaleQuantity / currentData.totalQuantity) * 100)
                  : 0}%</span>
              </div>
            </div>
          </div>

        </div>

        {/* Charts Section */}
        <div className="charts-section">
          <div className="chart-row">
            <div className="chart-container">
              <h3>Xu hướng lượng bán theo loại đơn (7 ngày)</h3>
              <div className="chart-wrapper">
                {loadingStates.dailySales ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <div className="loading-text">Đang tải dữ liệu...</div>
                  </div>
                ) : (
                  <Bar data={salesTrendData} options={chartOptions} />
                )}
              </div>
            </div>

            <div className="chart-container">
              <h3>Xu hướng lượng bán theo loại bánh (7 ngày)</h3>
              <div className="chart-wrapper">
                {loadingStates.dailySales ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <div className="loading-text">Đang tải dữ liệu...</div>
                  </div>
                ) : (
                  <Bar data={salesTrendByTypeData} options={chartOptions} />
                )}
              </div>
            </div>

            <div className="chart-container">
              <h3>Tồn theo loại bánh</h3>
              <div className="chart-wrapper">
                {loadingStates.inventory ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <div className="loading-text">Đang tải dữ liệu...</div>
                  </div>
                ) : (
                  <Doughnut data={cakeTypeDistribution} options={doughnutOptions} />
                )}
              </div>
            </div>
          </div>

          <div className="chart-row">
            <div className="chart-container">
              <h3>Xu hướng doanh số (7 ngày)</h3>
              <div className="chart-wrapper">
                {loadingStates.dailySales ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <div className="loading-text">Đang tải dữ liệu...</div>
                  </div>
                ) : (
                  <Line data={revenueData} options={lineChartOptions} />
                )}
              </div>
            </div>

            <div className="chart-container">
              <h3>Top 20 cửa hàng bán chạy nhất</h3>
              <div className="chart-wrapper">
                {loadingStates.topStores ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <div className="loading-text">Đang tải dữ liệu...</div>
                  </div>
                ) : (
                  <Bar data={topStoresChartData} options={topStoresOptions} />
                )}
              </div>
            </div>

            <div className="chart-container">
              <h3>Top 20 cửa hàng tồn nhiều bánh nhất</h3>
              <div className="chart-wrapper">
                {loadingStates.inventory ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <div className="loading-text">Đang tải dữ liệu...</div>
                  </div>
                ) : (
                  <Bar data={topInventoryStoresChartData} options={topInventoryStoresOptions} />
                )}
              </div>
            </div>
          </div>
        </div>

        {dashboardData.error && (
          <div style={{ 
            color: 'red', 
            textAlign: 'center', 
            padding: '20px',
            backgroundColor: '#fee2e2',
            borderRadius: '8px',
            margin: '20px 0'
          }}>
            {dashboardData.error}
          </div>
        )}
      </div>
    </>
  );
};

export default DashboardBkem;
